// Settings Card Component
// Displays user settings with developer mode toggle and sub-options

import React, { useState, useEffect } from 'react';
import { RevoltSwitch } from '../../../components';
import { envUtils } from '../../../core/utils';
import { RevoltInput } from '../../../components/RevoltInput';
import './SettingsCard.css';

export function SettingsCard({ user }) {
  const isAdmin = user?.role === 'admin';
  const fallbackActiveModelId = 'google/gemini-3-flash-preview';

  const [developerMode, setDeveloperMode] = useState(false);
  const [easyAppManagement, setEasyAppManagement] = useState(false);
  const [detailedViewInfo, setDetailedViewInfo] = useState(false);
  const [adminAppData, setAdminAppData] = useState(false);

  // Default model for new apps
  const [activeModelId, setActiveModelId] = useState(null);
  const [modelSearchText, setModelSearchText] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState(null);
  const [showModelDropdown, setShowModelDropdown] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const uiMode = envUtils.device.getRecommendedUIMode();
    return uiMode === 'mobile';
  });

  // Track pending operations to prevent conflicts
  const [pendingOperations, setPendingOperations] = useState(new Set());

  // Debounced setting updater to prevent rapid successive calls
  const createDebouncedSettingUpdater = (settingKey, saveFunction, localSetter) => {
    let timeoutId = null;
    let pendingValue = null;

    return async (enabled) => {
      // Clear any pending operation for this setting
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Mark operation as pending
      setPendingOperations(prev => new Set(prev).add(settingKey));

      // Optimistic update
      localSetter(enabled);
      pendingValue = enabled;

      // Debounce the API call
      timeoutId = setTimeout(async () => {
        try {
          // Only save if this is still the most recent value
          if (pendingValue === enabled) {
            await saveFunction(enabled);
          }
        } catch (error) {
          // On error, revert the optimistic update
          localSetter(!enabled);
          console.error(`Failed to save ${settingKey}:`, error);
        } finally {
          // Remove from pending operations
          setPendingOperations(prev => {
            const next = new Set(prev);
            next.delete(settingKey);
            return next;
          });
          timeoutId = null;
          pendingValue = null;
        }
      }, 300); // 300ms debounce
    };
  };

  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      const uiMode = envUtils.device.getRecommendedUIMode();
      setIsMobile(uiMode === 'mobile');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Load raw settings for display/configuration
        const response = await fetch(
          `/api/account/settings`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            // Extract our settings from the bulk response
            const settings = data.settings;
            setDeveloperMode(Boolean(settings.developerMode));
            setEasyAppManagement(Boolean(settings.easyAppManagement));
            setDetailedViewInfo(Boolean(settings.detailedViewInfo));
            setAdminAppData(Boolean(settings.adminAppData));

            // Default model for new apps (used by workspace new-app model auto selection)
            setActiveModelId(
              settings.activeModel !== undefined && settings.activeModel !== null && settings.activeModel !== ''
                ? settings.activeModel
                : null
            );
          }
        } else {
          // API failed, keep defaults
          console.warn('Failed to load user settings:', response.status);
        }
      } catch (error) {
        console.warn('Failed to load user settings:', error);
        // Keep default values (false)
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [user?.id]);

  // Load available OpenRouter models for the default-model search dropdown
  useEffect(() => {
    let cancelled = false;

    const loadModels = async () => {
      try {
        setModelsLoading(true);
        setModelsError(null);

        const response = await fetch('/api/openrouter/models', {
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || `HTTP error ${response.status}`);
        }

        if (cancelled) return;
        setAvailableModels(data.models || []);
      } catch (err) {
        if (cancelled) return;
        setModelsError(err?.message || 'Failed to load models');
        setAvailableModels([]);
      } finally {
        if (cancelled) return;
        setModelsLoading(false);
      }
    };

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const effectiveActiveModelId = activeModelId || fallbackActiveModelId;
  const getModelLabel = (modelId) => {
    const model = availableModels.find((m) => m.id === modelId);
    if (model) return model.name || model.id;
    return modelId;
  };

  const selectedModel = availableModels.find(
    (m) => m.id === effectiveActiveModelId
  );
  const formatPricePerMillion = (value) => {
    if (typeof value !== "number") return "—";
    return `$${(value * 1000000).toFixed(2)}`;
  };

  const saveActiveModel = async (modelId) => {
    const response = await fetch(`/api/account/settings/activeModel`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ value: modelId }),
    });

    if (!response.ok) {
      throw new Error('Failed to save active model');
    }
  };

  // Save developer mode setting
  const saveDeveloperMode = async (enabled) => {
    const response = await fetch(
      `/api/account/settings/developerMode`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: enabled }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to save developer mode setting');
    }
  };

  // Save detailed view info setting
  const saveDetailedViewInfo = async (enabled) => {
    const response = await fetch(
      `/api/account/settings/detailedViewInfo`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: enabled }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to save detailed view info setting');
    }
  };

  // Save easy app management setting
  const saveEasyAppManagement = async (enabled) => {
    const response = await fetch(
      `/api/account/settings/easyAppManagement`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: enabled }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to save easy app management setting');
    }
  };

  // Save admin app data setting
  const saveAdminAppData = async (enabled) => {
    const response = await fetch(
      `/api/account/settings/adminAppData`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: enabled }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to save admin app data setting');
    }
  };

  // Function to reload settings from server
  const reloadSettings = async () => {
    try {
      const response = await fetch(
        `/api/account/settings`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          const settings = data.settings;
          setDeveloperMode(Boolean(settings.developerMode));
          setEasyAppManagement(Boolean(settings.easyAppManagement));
          setDetailedViewInfo(Boolean(settings.detailedViewInfo));
          setAdminAppData(Boolean(settings.adminAppData));

          setActiveModelId(
            settings.activeModel !== undefined &&
              settings.activeModel !== null &&
              settings.activeModel !== ''
              ? settings.activeModel
              : null
          );
        }
      }
    } catch (error) {
      console.warn('Failed to reload user settings:', error);
    }
  };

  // Create debounced updater for detailed view info
  const updateDetailedViewInfo = createDebouncedSettingUpdater(
    'detailedViewInfo',
    saveDetailedViewInfo,
    setDetailedViewInfo
  );

  // Create debounced updater for easy app management (developer mode needs special handling)
  const updateEasyAppManagement = createDebouncedSettingUpdater(
    'easyAppManagement',
    saveEasyAppManagement,
    setEasyAppManagement
  );

  // Create debounced updater for admin app data
  const updateAdminAppData = createDebouncedSettingUpdater(
    'adminAppData',
    saveAdminAppData,
    setAdminAppData
  );

  const handleDeveloperModeToggle = async (enabled) => {
    // For developer mode, we need special handling since it affects sub-settings visibility
    // Don't use the debounced updater for this one since we need immediate reload
    setDeveloperMode(enabled);

    // Mark as pending
    setPendingOperations(prev => new Set(prev).add('developerMode'));

    try {
      await saveDeveloperMode(enabled);
      // Reload all settings after successful save
      await reloadSettings();
    } catch (error) {
      // Revert on error
      setDeveloperMode(!enabled);
      console.error('Failed to save developer mode:', error);
    } finally {
      setPendingOperations(prev => {
        const next = new Set(prev);
        next.delete('developerMode');
        return next;
      });
    }
  };

  const computeModelScore = (query, model) => {
    const q = query.trim().toLowerCase();
    if (!q) return 0;

    const name = (model?.name || model?.id || '').toLowerCase();
    const id = (model?.id || '').toLowerCase();

    if (name === q || id === q) return 1000;
    if (name.startsWith(q)) return 900;
    if (id.startsWith(q)) return 850;
    if (name.includes(q)) return 700;
    if (id.includes(q)) return 650;

    return 0;
  };

  const filteredModels = (() => {
    const q = modelSearchText.trim();
    if (!q) return [];

    const scored = availableModels
      .map((model) => ({
        model,
        score: computeModelScore(q, model),
      }))
      .filter((x) => x.score > 0);

    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aName = (a.model.name || a.model.id || '').toLowerCase();
      const bName = (b.model.name || b.model.id || '').toLowerCase();
      return aName.localeCompare(bName);
    });

    return scored.slice(0, 12).map((x) => x.model);
  })();

  const handlePickModel = async (model) => {
    if (!model?.id) return;
    setShowModelDropdown(false);
    setActiveModelId(model.id);
    // Keep the search box empty; selected model is shown above.
    setModelSearchText('');

    try {
      await saveActiveModel(model.id);
    } catch (error) {
      // Revert if save fails.
      await reloadSettings();
      setShowModelDropdown(false);
      console.error('Failed to save active model:', error);
    }
  };

  return (
    <div className="account-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-gear"></i> Settings
        </h2>
      </div>
      <div className="card-body">
        {isLoading ? (
          <div className="settings-loading">
            <div className="settings-loading-spinner">
              <i className="fa-solid fa-spinner fa-spin"></i>
            </div>
            <div className="settings-loading-text">Loading settings...</div>
          </div>
        ) : (
          <>
            <div className="settings-sections">
              {/* Models Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Models</h3>
                </div>
                <div className="settings-section-content">
                  <div className="settings-item">
                    <div className="settings-model-card">
                      {(modelsLoading || availableModels.length === 0) ? (
                        <div className="settings-model-card-loading">
                          <div className="settings-model-card-loading-title" />
                          <div className="settings-model-card-loading-provider" />
                          <div className="settings-model-card-loading-grid">
                            <div className="settings-model-card-loading-block" />
                            <div className="settings-model-card-loading-block" />
                            <div className="settings-model-card-loading-block" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="settings-model-card-top">
                            <div className="settings-model-card-title-row">
                              <div className="settings-model-card-provider">
                                {selectedModel?.provider || 'Unknown provider'}
                              </div>
                              <div className="settings-model-card-title">
                                {getModelLabel(effectiveActiveModelId)}
                              </div>
                            </div>
                          </div>

                          <div className="settings-model-card-grid">
                            <div className="settings-model-card-metric">
                              <span className="settings-model-card-metric-label">
                                Context
                              </span>
                              <span className="settings-model-card-metric-value">
                                {selectedModel?.context_length
                                  ? selectedModel.context_length.toLocaleString()
                                  : '—'}
                              </span>
                            </div>
                            <div className="settings-model-card-metric">
                              <span className="settings-model-card-metric-label">
                                Input
                              </span>
                              <span className="settings-model-card-metric-value">
                                {formatPricePerMillion(
                                  selectedModel?.pricingNumeric?.prompt
                                )}
                                /M
                              </span>
                            </div>
                            <div className="settings-model-card-metric">
                              <span className="settings-model-card-metric-label">
                                Output
                              </span>
                              <span className="settings-model-card-metric-value">
                                {formatPricePerMillion(
                                  selectedModel?.pricingNumeric?.completion
                                )}
                                /M
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="settings-model-input-wrap">
                      <div className="settings-item-main">
                        <RevoltInput
                          type="text"
                          placeholder="Search models..."
                          value={modelSearchText}
                          onChange={(e) => {
                            setModelSearchText(e.target.value);
                            setShowModelDropdown(true);
                          }}
                          onFocus={() => setShowModelDropdown(true)}
                          onBlur={() => {
                            // Allow dropdown click to register before closing.
                            setTimeout(() => setShowModelDropdown(false), 120);
                          }}
                        />
                      </div>

                      {showModelDropdown && (
                        <div className="settings-model-dropdown">
                          {modelsLoading ? (
                            <div className="settings-model-dropdown-item disabled">
                              Loading models...
                            </div>
                          ) : modelsError ? (
                            <div className="settings-model-dropdown-item disabled">
                              {modelsError}
                            </div>
                          ) : filteredModels.length === 0 ? (
                            <div className="settings-model-dropdown-item disabled">
                              No matching models
                            </div>
                          ) : (
                            filteredModels.map((model) => (
                              <button
                                key={model.id}
                                type="button"
                                className="settings-model-dropdown-item"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handlePickModel(model)}
                                title={model.id}
                              >
                                <span className="settings-model-dropdown-item-title">
                                  {model.name || model.id}
                                </span>
                                <span className="settings-model-dropdown-item-sub">
                                  {model.provider || ''}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Developer Mode Section */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h3 className="settings-section-title">Developer</h3>
                </div>
                <div className="settings-section-content">
                  <div className="settings-item">
                    {isMobile ? (
                      // Mobile: Stacked layout
                      <div className="settings-item-content">
                        <div className="settings-item-main">
                          <RevoltSwitch
                            checked={developerMode}
                            onChange={handleDeveloperModeToggle}
                            label="Developer Mode"
                            disabled={pendingOperations.has('developerMode')}
                          />
                        </div>
                        <div className="settings-item-description">
                          Enable developer features and advanced options for power users.
                        </div>
                      </div>
                    ) : (
                      // Desktop: Horizontal layout
                      <div className="settings-item-horizontal">
                        <RevoltSwitch
                          checked={developerMode}
                          onChange={handleDeveloperModeToggle}
                          disabled={pendingOperations.has('developerMode')}
                        />
                        <div className="settings-item-label">Developer Mode</div>
                        <div className="settings-item-description">
                          Enable developer features and advanced options for power users.
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Developer Mode Sub-options */}
                  {developerMode && (
                    <div className="settings-sub-options">
                      <div className="settings-sub-option">
                        {isMobile ? (
                          // Mobile: Stacked layout
                          <>
                            <RevoltSwitch
                              checked={easyAppManagement}
                              onChange={updateEasyAppManagement}
                              label="Easy App Management"
                              disabled={pendingOperations.has('easyAppManagement')}
                            />
                            <div className="settings-sub-description">
                              When right-clicking on an app, you will gain the option of deleting the app directly from the app card in the app section.
                            </div>
                          </>
                        ) : (
                          // Desktop: Horizontal layout
                          <div className="settings-item-horizontal">
                            <RevoltSwitch
                              checked={easyAppManagement}
                              onChange={updateEasyAppManagement}
                              disabled={pendingOperations.has('easyAppManagement')}
                            />
                            <div className="settings-item-label">Easy App Management</div>
                            <div className="settings-item-description">
                              When right-clicking on an app, you will gain the option of deleting the app directly from the app card in the app section.
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="settings-sub-option">
                        {isMobile ? (
                          <>
                            <RevoltSwitch
                              checked={detailedViewInfo}
                              onChange={updateDetailedViewInfo}
                              label="Detailed View Information"
                              disabled={pendingOperations.has('detailedViewInfo')}
                            />
                            <div className="settings-sub-description">
                              Show view breakdown (Direct, Sharing, Embed, etc.) in app settings Sharing tab.
                            </div>
                          </>
                        ) : (
                          <div className="settings-item-horizontal">
                            <RevoltSwitch
                              checked={detailedViewInfo}
                              onChange={updateDetailedViewInfo}
                              disabled={pendingOperations.has('detailedViewInfo')}
                            />
                            <div className="settings-item-label">Detailed View Information</div>
                            <div className="settings-item-description">
                              Show view breakdown (Direct, Sharing, Embed, etc.) in app settings Sharing tab.
                            </div>
                          </div>
                        )}
                      </div>
                      {isAdmin && (
                        <div className="settings-sub-option">
                          {isMobile ? (
                            <>
                              <RevoltSwitch
                                checked={adminAppData}
                                onChange={updateAdminAppData}
                                label="Admin App Data"
                                disabled={pendingOperations.has('adminAppData')}
                              />
                              <div className="settings-sub-description">
                                Show admin-only app and chat identifiers in developer-facing workspace tools.
                              </div>
                            </>
                          ) : (
                            <div className="settings-item-horizontal">
                              <RevoltSwitch
                                checked={adminAppData}
                                onChange={updateAdminAppData}
                                disabled={pendingOperations.has('adminAppData')}
                              />
                              <div className="settings-item-label">Admin App Data</div>
                              <div className="settings-item-description">
                                Show admin-only app and chat identifiers in developer-facing workspace tools.
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}